import { CallStatusEnum } from "@/lib/enum/call-status.enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import callService from "@/services/shared/callService";
import {
  MrfiktivRealtimeSessionViewModelGen,
  MrfiktivTranscriptItemGen,
  MrfiktivToolCallGen,
  MrfiktivCallTrackingViewModelGen,
  MrfiktivRealtimeSessionControllerGetUsageParamsGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { CallDataAccessLayer } from "@/store/modules/access-layers/call.access-layer";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { $t } from "@/lib/utility/t";
import { IEntity } from "@/lib/utility/data/entity.interface";

@IsFilterable
class CallBase implements IEntity<ICall, MrfiktivCallTrackingViewModelGen>, MrfiktivRealtimeSessionViewModelGen {
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.call.id"
  })
  id: string;

  partnerId: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.call.from"
  })
  from: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.call.to"
  })
  to: string;

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.call.status",
    config: {
      items: Object.values(CallStatusEnum).map((v) => ({
        text: `enums.CallStatusEnum.${v}`,
        value: v
      })),
      itemValue: "value"
    }
  })
  status: CallStatusEnum;

  startedAt: string;

  get startedAtDate() {
    return new Date(this.startedAt);
  }

  endedAt?: string;

  get endedAtDate() {
    if (this.endedAt) {
      return new Date(this.endedAt);
    }
  }

  duration?: number;

  summary?: string;

  personId?: string;

  callSid?: string;

  realtimeSessionId?: string;

  transcriptItems?: MrfiktivTranscriptItemGen[];

  toolCalls?: MrfiktivToolCallGen[];

  callTracking?: MrfiktivCallTrackingViewModelGen[];

  metadata?: object;

  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  loading = false;

  get displayTitle(): string {
    if (this.summary) {
      return this.summary.length > 140 ? this.preview + "..." : this.preview;
    }

    return this.from || this.id;
  }

  get preview() {
    if (!this.summary) {
      return "";
    }

    const body = this.summary.replace("<br>", " ");
    return body.replace(/<[^>]+>/g, "").slice(0, 140);
  }

  get statusColor(): string {
    switch (this.status) {
      case CallStatusEnum.ACTIVE:
        return "info";
      case CallStatusEnum.COMPLETED:
        return "success";
      case CallStatusEnum.FAILED:
        return "error";
      case CallStatusEnum.TERMINATED:
        return "warning";
      default:
        return "grey";
    }
  }

  /**
   * Gets the readable status text
   */
  get statusReadable() {
    return $t(`enums.CallStatusEnum.${this.status}`);
  }

  get durationFormatted(): string {
    if (this.duration == null) return "";
    const minutes = Math.floor(this.duration / 60);
    const seconds = this.duration % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  }

  constructor(call?: Partial<MrfiktivRealtimeSessionViewModelGen>) {
    this.id = call?.id ?? "";
    this.partnerId = call?.partnerId ?? "";
    this.from = call?.from ?? "";
    this.to = call?.to ?? "";
    this.status = (call?.status as CallStatusEnum) ?? CallStatusEnum.ACTIVE;
    this.startedAt = call?.startedAt ?? "";
    this.endedAt = call?.endedAt;
    this.duration = call?.duration;
    this.summary = call?.summary;
    this.personId = call?.personId;
    this.callSid = call?.callSid;
    this.realtimeSessionId = call?.realtimeSessionId;
    this.transcriptItems = call?.transcriptItems;
    this.toolCalls = call?.toolCalls;
    this.callTracking = call?.callTracking;
    this.metadata = call?.metadata;
    this.timestamp = new Timestamp(call?.timestamp);
  }

  map(call?: MrfiktivRealtimeSessionViewModelGen) {
    this.id = call?.id ?? "";
    this.partnerId = call?.partnerId ?? "";
    this.from = call?.from ?? "";
    this.to = call?.to ?? "";
    this.status = (call?.status as CallStatusEnum) ?? CallStatusEnum.ACTIVE;
    this.startedAt = call?.startedAt ?? "";
    this.endedAt = call?.endedAt;
    this.duration = call?.duration;
    this.summary = call?.summary;
    this.personId = call?.personId;
    this.callSid = call?.callSid;
    this.realtimeSessionId = call?.realtimeSessionId;
    this.transcriptItems = call?.transcriptItems;
    this.toolCalls = call?.toolCalls;
    this.callTracking = call?.callTracking;
    this.metadata = call?.metadata;
    this.timestamp = new Timestamp(call?.timestamp);
  }

  async fetch(): Promise<this> {
    this.loading = true;
    try {
      const res = await callService.getOne(this.partnerId, this.id);

      this.map(res);

      CallDataAccessLayer.set(this);

      return this;
    } finally {
      this.loading = false;
    }
  }

  static getUsage(query: MrfiktivRealtimeSessionControllerGetUsageParamsGen) {
    return callService.getUsage(query);
  }
}

type ICall = CallBase;
const Call = Filter.createForClass(CallBase);

export { ICall, Call };
